/**
 * Продажи и выход на рынки (ТЗ 6.29, 6.30, 6.34): счета и оплаты лицензий (ЛЦ-06), ключ установки у заказчика
 * (ЛЦ-07), брендирование и региональные настройки компании (ЛЦ-08, МР-05), самостоятельная регистрация (ГЛ-05),
 * страны, налоги, валюты, правовые документы, партнёры, санкции (МР-01…МР-28).
 * Ничего про конкретную компанию: реквизиты продавца, ставки и тексты — в данных платформы.
 */
import { z } from 'zod';
import { LICENSE_KINDS, PlanLimits } from './licensing';

const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата — ГГГГ-ММ-ДД');
const Money = z.number().nonnegative().max(1e12);

/* ───────────────────────── Расчёт суммы счёта ───────────────────────── */

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Сумма счёта: цена за период, налог либо внутри цены, либо сверху (МР-15). Округление до сотых — один раз,
 * итог всегда равен «без налога + налог».
 */
export function priceInvoice(
  unitPrice: number,
  months: number,
  vatRate: number,
  taxIncluded: boolean,
): { net: number; vat: number; total: number } {
  const gross = round2(unitPrice * months);
  if (vatRate <= 0) return { net: gross, vat: 0, total: gross };
  if (taxIncluded) {
    const net = round2(gross / (1 + vatRate / 100));
    return { net, vat: round2(gross - net), total: gross };
  }
  const vat = round2((gross * vatRate) / 100);
  return { net: gross, vat, total: round2(gross + vat) };
}

/** Прибавить месяцы к дате ГГГГ-ММ-ДД: конец месяца сохраняется (31 января + 1 месяц = 28/29 февраля) */
export function addMonthsIso(date: string, months: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  return `${ny}-${String(nm).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

/* ───────────────────────── Сумма прописью (МР-06) ───────────────────────── */

export const DOC_LANGS = ['ru', 'kk', 'en'] as const;
export type DocLang = (typeof DOC_LANGS)[number];

const RU_ONES = [
  ['', ''],
  ['один', 'одна'],
  ['два', 'две'],
  ['три', 'три'],
  ['четыре', 'четыре'],
  ['пять', 'пять'],
  ['шесть', 'шесть'],
  ['семь', 'семь'],
  ['восемь', 'восемь'],
  ['девять', 'девять'],
] as const;
const RU_TEENS = [
  'десять',
  'одиннадцать',
  'двенадцать',
  'тринадцать',
  'четырнадцать',
  'пятнадцать',
  'шестнадцать',
  'семнадцать',
  'восемнадцать',
  'девятнадцать',
];
const RU_TENS = [
  '',
  '',
  'двадцать',
  'тридцать',
  'сорок',
  'пятьдесят',
  'шестьдесят',
  'семьдесят',
  'восемьдесят',
  'девяносто',
];
const RU_HUND = [
  '',
  'сто',
  'двести',
  'триста',
  'четыреста',
  'пятьсот',
  'шестьсот',
  'семьсот',
  'восемьсот',
  'девятьсот',
];
const RU_SCALES: Array<[string, string, string, boolean]> = [
  ['тысяча', 'тысячи', 'тысяч', true],
  ['миллион', 'миллиона', 'миллионов', false],
  ['миллиард', 'миллиарда', 'миллиардов', false],
  ['триллион', 'триллиона', 'триллионов', false],
];

function ruPlural(n: number, one: string, few: string, many: string): string {
  const a = n % 100;
  const b = n % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

function ruTriplet(n: number, fem: boolean): string[] {
  const out: string[] = [];
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const o = n % 10;
  if (h) out.push(RU_HUND[h]!);
  if (t === 1) out.push(RU_TEENS[o]!);
  else {
    if (t) out.push(RU_TENS[t]!);
    if (o) out.push(RU_ONES[o]![fem ? 1 : 0]!);
  }
  return out;
}

function ruInt(n: number, fem: boolean): string {
  if (n === 0) return 'ноль';
  const parts: string[] = [];
  const triplets: number[] = [];
  let rest = n;
  while (rest > 0) {
    triplets.push(rest % 1000);
    rest = Math.floor(rest / 1000);
  }
  for (let i = triplets.length - 1; i >= 0; i--) {
    const v = triplets[i]!;
    if (!v) continue;
    if (i === 0) parts.push(...ruTriplet(v, fem));
    else {
      const sc = RU_SCALES[i - 1]!;
      parts.push(...ruTriplet(v, sc[3]), ruPlural(v, sc[0], sc[1], sc[2]));
    }
  }
  return parts.join(' ');
}

const KK_ONES = ['', 'бір', 'екі', 'үш', 'төрт', 'бес', 'алты', 'жеті', 'сегіз', 'тоғыз'];
const KK_TENS = ['', 'он', 'жиырма', 'отыз', 'қырық', 'елу', 'алпыс', 'жетпіс', 'сексен', 'тоқсан'];
const KK_SCALES = ['', 'мың', 'миллион', 'миллиард', 'триллион'];

function kkInt(n: number): string {
  if (n === 0) return 'нөл';
  const parts: string[] = [];
  const triplets: number[] = [];
  let rest = n;
  while (rest > 0) {
    triplets.push(rest % 1000);
    rest = Math.floor(rest / 1000);
  }
  for (let i = triplets.length - 1; i >= 0; i--) {
    const v = triplets[i]!;
    if (!v) continue;
    const h = Math.floor(v / 100);
    const t = Math.floor((v % 100) / 10);
    const o = v % 10;
    if (h) parts.push(h === 1 ? 'жүз' : `${KK_ONES[h]} жүз`);
    if (t) parts.push(KK_TENS[t]!);
    if (o && !(i > 0 && v === 1 && i === 1)) parts.push(KK_ONES[o]!);
    else if (o && i === 1) parts.push('бір');
    if (i > 0) parts.push(KK_SCALES[i]!);
  }
  return parts.join(' ');
}

const EN_ONES = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const EN_SCALES = ['', 'thousand', 'million', 'billion', 'trillion'];

function enInt(n: number): string {
  if (n === 0) return 'zero';
  const parts: string[] = [];
  const triplets: number[] = [];
  let rest = n;
  while (rest > 0) {
    triplets.push(rest % 1000);
    rest = Math.floor(rest / 1000);
  }
  for (let i = triplets.length - 1; i >= 0; i--) {
    const v = triplets[i]!;
    if (!v) continue;
    const h = Math.floor(v / 100);
    const r = v % 100;
    if (h) parts.push(`${EN_ONES[h]} hundred`);
    if (r) {
      parts.push(
        r < 20 ? EN_ONES[r]! : `${EN_TENS[Math.floor(r / 10)]}${r % 10 ? `-${EN_ONES[r % 10]}` : ''}`,
      );
    }
    if (i > 0) parts.push(EN_SCALES[i]!);
  }
  return parts.join(' ');
}

/** Названия валют для суммы прописью: [единица (1), (2–4), (5+), младшая единица ×3, род старшей] */
const CURRENCY_WORDS: Record<
  string,
  {
    ru: [string, string, string, boolean, string, string, string];
    kk: [string, string];
    en: [string, string];
  }
> = {
  KZT: {
    ru: ['тенге', 'тенге', 'тенге', false, 'тиын', 'тиына', 'тиынов'],
    kk: ['теңге', 'тиын'],
    en: ['tenge', 'tiyn'],
  },
  USD: {
    ru: ['доллар США', 'доллара США', 'долларов США', false, 'цент', 'цента', 'центов'],
    kk: ['АҚШ доллары', 'цент'],
    en: ['US dollars', 'cents'],
  },
  EUR: {
    ru: ['евро', 'евро', 'евро', false, 'цент', 'цента', 'центов'],
    kk: ['еуро', 'цент'],
    en: ['euros', 'cents'],
  },
  RUB: {
    ru: ['рубль', 'рубля', 'рублей', false, 'копейка', 'копейки', 'копеек'],
    kk: ['рубль', 'тиын'],
    en: ['roubles', 'kopecks'],
  },
};

/** Сумма прописью на языке документа; валюта вне списка — по коду (МР-06) */
export function amountInWords(amount: number, currency: string, lang: DocLang): string {
  const cents = Math.round(Math.abs(amount) * 100);
  const whole = Math.floor(cents / 100);
  const frac = String(cents % 100).padStart(2, '0');
  const cw = CURRENCY_WORDS[currency.toUpperCase()];
  const sign = amount < 0 ? (lang === 'ru' ? 'минус ' : lang === 'kk' ? 'минус ' : 'minus ') : '';
  let text: string;
  if (lang === 'ru') {
    const w = cw?.ru ?? [currency, currency, currency, false, '', '', ''];
    text = `${ruInt(whole, false)} ${ruPlural(whole, w[0], w[1], w[2])} ${frac} ${ruPlural(Number(frac), w[4], w[5], w[6]) || ''}`;
  } else if (lang === 'kk') {
    const w = cw?.kk ?? [currency, ''];
    text = `${kkInt(whole)} ${w[0]} ${frac} ${w[1]}`;
  } else {
    const w = cw?.en ?? [currency, ''];
    text = `${enInt(whole)} ${w[0]} and ${frac}/100 ${w[1]}`;
  }
  const out = `${sign}${text}`.replace(/\s+/g, ' ').trim();
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/** Перевод суммы по курсам к тенге (курс — сколько тенге стоит единица валюты); тенге — курс 1 (МР-06) */
export function convertCurrency(
  amount: number,
  from: string,
  to: string,
  rates: Record<string, number>,
): number | null {
  if (from === to) return amount;
  const r = (c: string) => (c === 'KZT' ? 1 : rates[c]);
  const a = r(from);
  const b = r(to);
  if (!a || !b) return null;
  return round2((amount * a) / b);
}

/* ───────────────────────── Реквизиты продавца и счета (ЛЦ-06) ───────────────────────── */

export const SellerSettings = z.object({
  name: z.string().trim().min(2).max(300),
  countryCode: z.string().trim().length(2).toUpperCase().default('KZ'),
  /** БИН/ИНН/регистрационный номер — как принято в стране продавца */
  taxId: z.string().trim().max(40).default(''),
  address: z.string().trim().max(500).default(''),
  bank: z.string().trim().max(200).default(''),
  account: z.string().trim().max(60).default(''),
  bankCode: z.string().trim().max(40).default(''),
  /** Код назначения платежа и прочие поля страны */
  extra: z.string().trim().max(200).default(''),
  signer: z.string().trim().max(200).default(''),
  email: z.string().trim().max(200).default(''),
  /** Префикс номера счёта: «СЧ» → СЧ-2026-0001 */
  invoicePrefix: z.string().trim().min(1).max(10).default('INV'),
  /** Срок оплаты счёта, дней */
  dueDays: z.number().int().min(1).max(90).default(10),
  /** Налог в счёте: код налога из справочника страны или пусто — без налога */
  taxCode: z.string().trim().max(20).default('VAT'),
});
export type SellerSettings = z.infer<typeof SellerSettings>;

export const PAYMENT_METHODS = ['bank', 'kaspi', 'card', 'cash', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_TITLES: Record<PaymentMethod, string> = {
  bank: 'Банковский перевод',
  kaspi: 'Kaspi',
  card: 'Карта',
  cash: 'Наличные',
  other: 'Другое',
};

export const INVOICE_STATUSES = ['issued', 'paid', 'cancelled'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const INVOICE_STATUS_TITLES: Record<InvoiceStatus | 'overdue', string> = {
  issued: 'Ждёт оплаты',
  paid: 'Оплачен',
  cancelled: 'Отменён',
  overdue: 'Просрочен',
};

export const InvoicePayment = z.object({
  id: z.uuid(),
  amount: z.number(),
  paidOn: z.string(),
  method: z.enum(PAYMENT_METHODS),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type InvoicePayment = z.infer<typeof InvoicePayment>;

export const Invoice = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantName: z.string().nullable(),
  number: z.string(),
  planId: z.uuid(),
  planName: z.string(),
  period: z.enum(['month', 'year']),
  months: z.number().int(),
  currency: z.string(),
  amountNet: z.number(),
  vatRate: z.number(),
  vatAmount: z.number(),
  amountTotal: z.number(),
  paidTotal: z.number(),
  status: z.enum(INVOICE_STATUSES),
  /** Ждёт оплаты и срок прошёл */
  overdue: z.boolean(),
  issuedOn: z.string(),
  dueOn: z.string(),
  paidOn: z.string().nullable(),
  requestedBy: z.enum(['platform', 'company']),
  note: z.string().nullable(),
  seller: z.record(z.string(), z.unknown()),
  buyer: z.record(z.string(), z.unknown()),
  payments: z.array(InvoicePayment),
  amountInWords: z.string(),
  createdAt: z.string(),
});
export type Invoice = z.infer<typeof Invoice>;

export const InvoiceInput = z.object({
  planId: z.uuid(),
  period: z.enum(['month', 'year']),
  /** Сколько месяцев оплачивается при помесячной оплате; на год — всегда 12 */
  months: z.number().int().min(1).max(12).default(1),
  note: z.string().trim().max(500).nullable().optional(),
});
export type InvoiceInput = z.infer<typeof InvoiceInput>;

export const PaymentInput = z.object({
  amount: z.number().positive().max(1e12),
  paidOn: DateOnly,
  method: z.enum(PAYMENT_METHODS).default('bank'),
  reference: z.string().trim().max(120).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type PaymentInput = z.infer<typeof PaymentInput>;

/** Платёжные реквизиты покупателя: хранятся настройкой компании и попадают в счёт */
export const BillingDetails = z.object({
  name: z.string().trim().max(300).default(''),
  taxId: z.string().trim().max(40).default(''),
  address: z.string().trim().max(500).default(''),
  email: z.string().trim().max(200).default(''),
});
export type BillingDetails = z.infer<typeof BillingDetails>;

/* ───────────────────────── Ключ установки у заказчика (ЛЦ-07) ───────────────────────── */

export const LICENSE_KEY_PREFIX = 'EPC1';

export const LicenseKeyPayload = z.object({
  v: z.literal(1),
  /** Отпечаток открытого ключа, которым проверяется подпись */
  kid: z.string().min(1).max(40),
  jti: z.string().min(8).max(64),
  /** Когда выпущен: новый ключ не может быть старше действующего */
  iat: z.string(),
  company: z.object({ slug: z.string(), name: z.string() }),
  plan: z.object({
    code: z.string(),
    name: z.string(),
    modules: z.array(z.string()),
    limits: PlanLimits,
  }),
  kind: z.enum(LICENSE_KINDS),
  startsAt: DateOnly,
  endsAt: DateOnly.nullable(),
  seats: z.number().int().positive().nullable(),
  limitsOverride: PlanLimits.partial().nullable(),
  /** Код установки: ключ работает только на ней; null — на любой */
  installId: z.string().max(64).nullable(),
});
export type LicenseKeyPayload = z.infer<typeof LicenseKeyPayload>;

export const LicenseKeyIssueInput = z.object({
  /** Код установки заказчика (виден ему в «Лицензия»); без него ключ подойдёт любой установке */
  installId: z.string().trim().min(8).max(64).nullable().optional(),
});
export type LicenseKeyIssueInput = z.infer<typeof LicenseKeyIssueInput>;

export const LicenseKeyActivateInput = z.object({ key: z.string().trim().min(40).max(8000) });
export type LicenseKeyActivateInput = z.infer<typeof LicenseKeyActivateInput>;

export const LicenseKeyInfo = z.object({
  id: z.uuid(),
  jti: z.string(),
  kid: z.string(),
  issuedAt: z.string(),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  planName: z.string(),
  installId: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type LicenseKeyInfo = z.infer<typeof LicenseKeyInfo>;

/* ───────────────────────── Брендирование и региональные настройки (ЛЦ-08, МР-05) ───────────────────────── */

const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Цвет — #RRGGBB');

/** Логотип — небольшая растровая картинка внутри настройки: так его видно на входе без авторизации */
export const LOGO_MAX_CHARS = 200_000;
const LogoData = z
  .string()
  .max(LOGO_MAX_CHARS, 'Логотип слишком большой — до 150 КБ')
  .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/, 'Логотип — PNG, JPEG или WebP');

export const Branding = z.object({
  /** Название в шапке, на входе и в письмах; пусто — название компании */
  title: z.string().trim().max(80).default(''),
  logo: LogoData.nullable().default(null),
  primary: HexColor.nullable().default(null),
  accent: HexColor.nullable().default(null),
  /** Подпись в конце писем портала */
  emailFooter: z.string().trim().max(300).default(''),
});
export type Branding = z.infer<typeof Branding>;
export const NO_BRANDING: Branding = { title: '', logo: null, primary: null, accent: null, emailFooter: '' };

/** Что видно на входе без авторизации */
export const PublicBrand = z.object({
  slug: z.string(),
  name: z.string(),
  title: z.string(),
  logo: z.string().nullable(),
  primary: z.string().nullable(),
  accent: z.string().nullable(),
});
export type PublicBrand = z.infer<typeof PublicBrand>;

export const UNIT_SYSTEMS = ['metric', 'imperial'] as const;
export const Regional = z.object({
  lang: z.enum(DOC_LANGS).default('ru'),
  timezone: z.string().max(60).default('Asia/Almaty'),
  currency: z.string().trim().length(3).toUpperCase().default('KZT'),
  /** dmy — 31.12.2026; mdy — 12/31/2026; ymd — 2026-12-31 */
  dateFormat: z.enum(['dmy', 'mdy', 'ymd']).default('dmy'),
  /** space_comma — 1 234,56; comma_dot — 1,234.56 */
  numberFormat: z.enum(['space_comma', 'comma_dot']).default('space_comma'),
  units: z.enum(UNIT_SYSTEMS).default('metric'),
  phoneFormat: z.string().trim().max(40).default('+# ### ### ## ##'),
});
export type Regional = z.infer<typeof Regional>;

/** Форматирование по региональным настройкам компании (МР-05) */
export function formatDateRegional(iso: string, fmt: Regional['dateFormat']): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  if (fmt === 'mdy') return `${m}/${d}/${y}`;
  if (fmt === 'ymd') return `${y}-${m}-${d}`;
  return `${d}.${m}.${y}`;
}

export function formatNumberRegional(n: number, fmt: Regional['numberFormat'], digits = 2): string {
  const [int, frac] = Math.abs(n).toFixed(digits).split('.') as [string, string | undefined];
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, fmt === 'comma_dot' ? ',' : ' ');
  const sep = fmt === 'comma_dot' ? '.' : ',';
  return `${n < 0 ? '-' : ''}${grouped}${frac ? sep + frac : ''}`;
}

/** Меры: метры ↔ футы, миллиметры ↔ дюймы, литры ↔ галлоны (США) */
export const UNIT_FACTORS = { m_to_ft: 3.280839895, mm_to_in: 0.0393700787, l_to_gal: 0.264172052 } as const;

/* ───────────────────────── Самостоятельная регистрация (ГЛ-05) ───────────────────────── */

export const SignupInput = z.object({
  company: z.string().trim().min(2).max(200),
  name: z.string().trim().min(2).max(200),
  email: z.email().transform((s) => s.toLowerCase()),
  countryCode: z.string().trim().length(2).toUpperCase().default('KZ'),
  lang: z.enum(DOC_LANGS).default('ru'),
  /** Согласие с офертой и политикой данных — без него регистрации нет (МР-09) */
  acceptTerms: z.literal(true, { error: 'Нужно принять условия' }),
  /** Метки ссылки: канал, кампания, партнёр (МР-19) */
  channel: z.string().trim().max(60).optional(),
  campaign: z.string().trim().max(80).optional(),
  partner: z.string().trim().max(40).optional(),
  /** Поле-ловушка для роботов: у человека пустое */
  website: z.string().max(200).optional(),
});
export type SignupInput = z.infer<typeof SignupInput>;

export const SignupConfirmInput = z.object({ token: z.string().min(20).max(200) });
export type SignupConfirmInput = z.infer<typeof SignupConfirmInput>;

export const SignupConfirmed = z.object({
  /** Адрес принятия приглашения: человек задаёт пароль и входит в свою компанию */
  invitePath: z.string(),
  company: z.string(),
  trialDays: z.number().int(),
});
export type SignupConfirmed = z.infer<typeof SignupConfirmed>;

const CYR: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
  ә: 'a',
  ғ: 'g',
  қ: 'k',
  ң: 'n',
  ө: 'o',
  ұ: 'u',
  ү: 'u',
  һ: 'h',
  і: 'i',
};

/** Код компании из названия: латиница, цифры и дефис; без юридической формы */
export function slugifyCompany(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/(?<![\p{L}\p{N}])(тоо|ооо|ао|ип|llc|ltd|inc|gmbh)(?![\p{L}\p{N}])/gu, ' ')
    .split('')
    .map((c) => CYR[c] ?? c)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 28);
  const s = /^[a-z]/.test(base) ? base : `c-${base}`;
  return s.length >= 2 ? s : 'company';
}

/* ───────────────────────── Страны, налоги, валюты (МР-01…МР-07, МР-15, МР-23, МР-24) ───────────────────────── */

export const COUNTRY_STATUSES = ['closed', 'pilot', 'ready'] as const;
export type CountryStatus = (typeof COUNTRY_STATUSES)[number];
export const COUNTRY_STATUS_TITLES: Record<CountryStatus, string> = {
  closed: 'Закрыта',
  pilot: 'Пилот',
  ready: 'Готова к продаже',
};
export const SANCTION_STATUSES = ['allowed', 'permit', 'blocked'] as const;
export type SanctionStatus = (typeof SANCTION_STATUSES)[number];
export const SANCTION_TITLES: Record<SanctionStatus, string> = {
  allowed: 'Продажа разрешена',
  permit: 'Нужно разрешение юриста',
  blocked: 'Продажа закрыта',
};

/** Пункты чек-листа готовности страны (МР-02): пока все не закрыты, статус «готова к продаже» не ставится */
export const COUNTRY_CHECKLIST = [
  { code: 'language', title: 'Язык интерфейса и документов' },
  { code: 'currency_tax', title: 'Валюта и налоги' },
  { code: 'legal', title: 'Оферта и политика данных' },
  { code: 'data_region', title: 'Регион хранения данных' },
  { code: 'payment', title: 'Способ оплаты' },
  { code: 'support', title: 'Поддержка в часовом поясе страны' },
  { code: 'seller', title: 'Юрлицо-продавец' },
  { code: 'sanctions', title: 'Проверка по санкционным спискам' },
] as const;

export const ChecklistEntry = z.object({
  done: z.boolean(),
  note: z.string().max(500).optional(),
  at: z.string().optional(),
});

export const Country = z.object({
  code: z.string(),
  name: z.string(),
  nameEn: z.string(),
  languages: z.array(z.string()),
  currency: z.string(),
  legalPack: z.string().nullable(),
  dataRegion: z.string().nullable(),
  partnerId: z.uuid().nullable(),
  status: z.enum(COUNTRY_STATUSES),
  sanctions: z.enum(SANCTION_STATUSES),
  sanctionsDecision: z.string().nullable(),
  sellerEntity: z.string().nullable(),
  governingLaw: z.string().nullable(),
  jurisdiction: z.string().nullable(),
  checklist: z.record(z.string(), ChecklistEntry),
  /** Сколько пунктов чек-листа закрыто из скольких */
  checklistDone: z.number().int(),
  checklistTotal: z.number().int(),
  targetRevenue: z.number().nullable(),
  notes: z.string().nullable(),
});
export type Country = z.infer<typeof Country>;

export const CountryInput = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, 'Код страны — две латинские буквы')
    .transform((s) => s.toUpperCase()),
  name: z.string().trim().min(2).max(100),
  nameEn: z.string().trim().min(2).max(100),
  languages: z.array(z.string().trim().min(2).max(5)).max(10).default([]),
  currency: z.string().trim().length(3).toUpperCase(),
  legalPack: z.string().trim().max(20).nullable().optional(),
  dataRegion: z.string().trim().max(20).nullable().optional(),
});
export type CountryInput = z.infer<typeof CountryInput>;

export const CountryPatch = z.object({
  name: z.string().trim().min(2).max(100).optional(),
  nameEn: z.string().trim().min(2).max(100).optional(),
  languages: z.array(z.string().trim().min(2).max(5)).max(10).optional(),
  currency: z.string().trim().length(3).toUpperCase().optional(),
  legalPack: z.string().trim().max(20).nullable().optional(),
  dataRegion: z.string().trim().max(20).nullable().optional(),
  partnerId: z.uuid().nullable().optional(),
  status: z.enum(COUNTRY_STATUSES).optional(),
  sanctions: z.enum(SANCTION_STATUSES).optional(),
  /** Решение юриста: обязательно при «нужно разрешение» и «закрыта» */
  sanctionsDecision: z.string().trim().max(1000).nullable().optional(),
  sellerEntity: z.string().trim().max(300).nullable().optional(),
  governingLaw: z.string().trim().max(200).nullable().optional(),
  jurisdiction: z.string().trim().max(200).nullable().optional(),
  targetRevenue: z.number().nonnegative().nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
  checklist: z.record(z.string(), ChecklistEntry).optional(),
});
export type CountryPatch = z.infer<typeof CountryPatch>;

/** Страна в публичной форме: только готовые к продаже */
export const PublicCountry = z.object({
  code: z.string(),
  name: z.string(),
  nameEn: z.string(),
  languages: z.array(z.string()),
  currency: z.string(),
});
export type PublicCountry = z.infer<typeof PublicCountry>;

export const TaxRate = z.object({
  id: z.uuid(),
  countryCode: z.string(),
  code: z.string(),
  name: z.string(),
  rate: z.number(),
  includedInPrice: z.boolean(),
  validFrom: z.string(),
  validTo: z.string().nullable(),
  isActive: z.boolean(),
});
export type TaxRate = z.infer<typeof TaxRate>;

export const TaxRateInput = z.object({
  countryCode: z.string().trim().length(2).toUpperCase(),
  code: z.string().trim().min(2).max(20),
  name: z.string().trim().min(2).max(100),
  rate: z.number().min(0).max(100),
  includedInPrice: z.boolean().default(false),
  validFrom: DateOnly,
  validTo: DateOnly.nullable().optional(),
});
export type TaxRateInput = z.infer<typeof TaxRateInput>;

export const CurrencyRate = z.object({
  id: z.uuid(),
  currency: z.string(),
  rateDate: z.string(),
  rate: z.number(),
  source: z.string(),
});
export type CurrencyRate = z.infer<typeof CurrencyRate>;

export const CurrencyRateInput = z.object({
  currency: z.string().trim().length(3).toUpperCase(),
  rateDate: DateOnly,
  rate: z.number().positive().max(1e9),
  source: z.string().trim().max(40).default('manual'),
});
export type CurrencyRateInput = z.infer<typeof CurrencyRateInput>;

export const PlanPrice = z.object({
  id: z.uuid(),
  planId: z.uuid(),
  countryCode: z.string(),
  priceMonth: z.number().nullable(),
  priceYear: z.number().nullable(),
  currency: z.string(),
  taxIncluded: z.boolean(),
});
export type PlanPrice = z.infer<typeof PlanPrice>;

export const PlanPriceInput = z.object({
  countryCode: z.string().trim().length(2).toUpperCase(),
  priceMonth: Money.nullable().optional(),
  priceYear: Money.nullable().optional(),
  currency: z.string().trim().length(3).toUpperCase(),
  taxIncluded: z.boolean().default(false),
});
export type PlanPriceInput = z.infer<typeof PlanPriceInput>;

/** Регионы хранения данных (МР-11) */
export const DATA_REGIONS = ['KZ', 'EU', 'US', 'AE', 'CN', 'UZ'] as const;
export type DataRegion = (typeof DATA_REGIONS)[number];

/** Функции страны (МР-26): включаются только в компаниях этой страны */
export const COUNTRY_MODULES: Array<{ code: string; title: string; countries: string[] }> = [
  { code: 'kenml', title: 'Сметы KENML и нормы АГСК', countries: ['KZ'] },
  { code: 'esf', title: 'Электронные счета-фактуры (ЭСФ)', countries: ['KZ'] },
  { code: 'order562', title: 'Формы актов по приказу № 562', countries: ['KZ'] },
  { code: 'ecp_kz', title: 'ЭЦП НУЦ РК (NCALayer)', countries: ['KZ'] },
];

export function countryModuleEnabled(countryCode: string, module: string): boolean {
  const m = COUNTRY_MODULES.find((x) => x.code === module);
  return !m || m.countries.includes(countryCode.toUpperCase());
}

/* ───────────────────────── Правовые документы (МР-09) ───────────────────────── */

export const LEGAL_KINDS = ['offer', 'privacy', 'dpa', 'sla'] as const;
export type LegalKind = (typeof LEGAL_KINDS)[number];
export const LEGAL_KIND_TITLES: Record<LegalKind, string> = {
  offer: 'Оферта и лицензионное соглашение',
  privacy: 'Политика конфиденциальности',
  dpa: 'Соглашение об обработке данных (DPA)',
  sla: 'Условия уровня сервиса (SLA)',
};

export const LegalDoc = z.object({
  id: z.uuid(),
  countryCode: z.string().nullable(),
  kind: z.enum(LEGAL_KINDS),
  lang: z.string(),
  version: z.string(),
  title: z.string(),
  body: z.string(),
  isCurrent: z.boolean(),
  publishedAt: z.string(),
  acceptances: z.number().int(),
});
export type LegalDoc = z.infer<typeof LegalDoc>;

export const LegalDocInput = z.object({
  countryCode: z.string().trim().length(2).toUpperCase().nullable().optional(),
  kind: z.enum(LEGAL_KINDS),
  lang: z.string().trim().min(2).max(5),
  version: z.string().trim().min(1).max(20),
  title: z.string().trim().min(2).max(200),
  body: z.string().trim().min(20).max(200_000),
});
export type LegalDocInput = z.infer<typeof LegalDocInput>;

/* ───────────────────────── Партнёры, источники заявок, воронка (МР-16, МР-19, МР-25) ───────────────────────── */

export const Partner = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  countryCode: z.string().nullable(),
  contact: z.string().nullable(),
  commissionPct: z.number(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  companies: z.number().int(),
});
export type Partner = z.infer<typeof Partner>;

export const PartnerInput = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[a-z0-9][a-z0-9-]{1,30}$/, 'Код партнёра — латиница, цифры и дефис'),
  name: z.string().trim().min(2).max(200),
  countryCode: z.string().trim().length(2).toUpperCase().nullable().optional(),
  contact: z.string().trim().max(300).nullable().optional(),
  commissionPct: z.number().min(0).max(100).default(0),
  notes: z.string().trim().max(1000).nullable().optional(),
});
export type PartnerInput = z.infer<typeof PartnerInput>;
export const PartnerPatch = PartnerInput.omit({ code: true })
  .partial()
  .extend({ isActive: z.boolean().optional() });
export type PartnerPatch = z.infer<typeof PartnerPatch>;

export const SpendInput = z.object({
  month: DateOnly,
  channel: z.string().trim().min(1).max(60),
  campaign: z.string().trim().max(80).nullable().optional(),
  countryCode: z.string().trim().length(2).toUpperCase().nullable().optional(),
  amount: Money,
  currency: z.string().trim().length(3).toUpperCase().default('KZT'),
});
export type SpendInput = z.infer<typeof SpendInput>;

export const FunnelRow = z.object({
  key: z.string(),
  requests: z.number().int(),
  trials: z.number().int(),
  paying: z.number().int(),
  spendKzt: z.number(),
  /** Стоимость заявки в тенге; null — расходов нет или заявок нет */
  costPerRequest: z.number().nullable(),
});
export type FunnelRow = z.infer<typeof FunnelRow>;

export const Funnel = z.object({
  /** Сколько раз открывали демо и вошли в него за период */
  demos: z.number().int(),
  groupBy: z.enum(['country', 'channel', 'partner']),
  rows: z.array(FunnelRow),
});
export type Funnel = z.infer<typeof Funnel>;

export const CountryPanelRow = z.object({
  code: z.string(),
  name: z.string(),
  status: z.enum(COUNTRY_STATUSES),
  currency: z.string(),
  requests: z.number().int(),
  trials: z.number().int(),
  paying: z.number().int(),
  churned: z.number().int(),
  revenueLocal: z.number(),
  revenueKzt: z.number().nullable(),
  partnerShare: z.number().nullable(),
  targetRevenue: z.number().nullable(),
  /** Выручка за год минус цель; null — цели нет */
  deviation: z.number().nullable(),
});
export type CountryPanelRow = z.infer<typeof CountryPanelRow>;

export const PartnerReportRow = z.object({
  partnerId: z.uuid(),
  code: z.string(),
  name: z.string(),
  companies: z.number().int(),
  paidTotalKzt: z.number().nullable(),
  commissionKzt: z.number().nullable(),
});
export type PartnerReportRow = z.infer<typeof PartnerReportRow>;

/* ───────────────────────── Санкции и проверка клиента (МР-23) ───────────────────────── */

export const SanctionsImportInput = z.object({
  listCode: z.string().trim().min(2).max(20),
  /** По одному названию в строке; после запятой — номер записи в списке */
  text: z.string().min(2).max(2_000_000),
  replace: z.boolean().default(true),
});
export type SanctionsImportInput = z.infer<typeof SanctionsImportInput>;

export const ScreeningResult = z.object({
  allowed: z.boolean(),
  reason: z.string().nullable(),
  matches: z.array(z.object({ listCode: z.string(), name: z.string(), ref: z.string().nullable() })),
});
export type ScreeningResult = z.infer<typeof ScreeningResult>;

/** Название для сличения со списками: без регистра, знаков, юридической формы и лишних пробелов */
export function normalizeName(s: string): string {
  return s
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(
      /(?<![\p{L}\p{N}])(тоо|ооо|ао|зао|ип|llc|ltd|inc|gmbh|jsc|co|corp|company)(?![\p{L}\p{N}])/gu,
      ' ',
    )
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/* ───────────────────────── Запросы субъектов данных (МР-10) ───────────────────────── */

export const PRIVACY_KINDS = ['export', 'rectify', 'delete'] as const;
export const PRIVACY_KIND_TITLES: Record<(typeof PRIVACY_KINDS)[number], string> = {
  export: 'Выгрузка данных',
  rectify: 'Исправление данных',
  delete: 'Удаление данных',
};
export const PRIVACY_STATUSES = ['new', 'in_progress', 'done', 'rejected'] as const;
/** Срок ответа субъекту, дней: по закону РК и GDPR — 30 */
export const PRIVACY_DUE_DAYS = 30;

export const PrivacyRequest = z.object({
  id: z.uuid(),
  kind: z.enum(PRIVACY_KINDS),
  subjectName: z.string(),
  subjectEmail: z.string(),
  status: z.enum(PRIVACY_STATUSES),
  receivedOn: z.string(),
  dueOn: z.string(),
  overdue: z.boolean(),
  result: z.string().nullable(),
  createdAt: z.string(),
});
export type PrivacyRequest = z.infer<typeof PrivacyRequest>;

export const PrivacyRequestInput = z.object({
  kind: z.enum(PRIVACY_KINDS),
  subjectName: z.string().trim().min(2).max(200),
  subjectEmail: z.email().transform((s) => s.toLowerCase()),
  receivedOn: DateOnly.optional(),
});
export type PrivacyRequestInput = z.infer<typeof PrivacyRequestInput>;

export const PrivacyRequestPatch = z.object({
  status: z.enum(PRIVACY_STATUSES),
  result: z.string().trim().max(2000).nullable().optional(),
});
export type PrivacyRequestPatch = z.infer<typeof PrivacyRequestPatch>;

export const Subprocessor = z.object({
  name: z.string().trim().min(2).max(120),
  purpose: z.string().trim().min(2).max(300),
  country: z.string().trim().max(60),
});
export type Subprocessor = z.infer<typeof Subprocessor>;
